'use client';

import { memo, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Volume2, VolumeX, X as XIcon } from 'lucide-react';

import type { StoryGroup } from '@nutralab/api';
import { EditorText, Icon, IconButton, Modal } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './StoryViewer.module.scss';

import { ModalProps } from '@/shared/types';
import { getImagePath, getImageUrl, sanitizeHtml } from '@/shared/utils';

const IMAGE_DURATION_MS = 5000;
const PROGRESS_TICK_MS = 50;
const VIDEO_LOAD_TIMEOUT_MS = 8000;

export interface StoryViewerData {
    groups: StoryGroup[];
    initialIndex: number;
    initialSlideIndex: number;
    onSlideViewed: (url: string) => void;
}

type StoryViewerProps = ModalProps<StoryViewerData>;

/** Full-screen viewer of a story group: images and videos with progress bars, previous / next and a caption. */
export function StoryViewer({ onClose, zIndex, data }: StoryViewerProps): ReactElement | null {
    const { t } = useTranslation();
    const { groups, initialIndex, initialSlideIndex, onSlideViewed } = data;

    const [slideIndex, setSlideIndex] = useState(initialSlideIndex);
    const [progress, setProgress] = useState(0);
    const [muted, setMuted] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const videoRef = useRef<HTMLVideoElement>(null);

    const group = groups[initialIndex];
    const media = group?.media ?? [];
    const slide = media[slideIndex];
    const nextSlide = media[slideIndex + 1];
    const description = useMemo(() => withExternalLinks(slide?.description ?? null), [slide?.description]);

    const goNext = (): void => {
        if (slide) {
            onSlideViewed(getImagePath(slide.url));
        }
        if (slideIndex < media.length - 1) {
            setSlideIndex((i) => i + 1);
        } else {
            onClose();
        }
    };

    const goPrev = (): void => {
        if (slideIndex > 0) {
            setSlideIndex((i) => i - 1);
        }
    };

    useEffect(() => {
        setProgress(0);
        setIsLoading(slide?.type === 'video');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slideIndex]);

    useEffect(() => {
        if (slide?.type !== 'video' || !isLoading) {
            return;
        }

        const id = setTimeout(goNext, VIDEO_LOAD_TIMEOUT_MS);
        return (): void => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slideIndex, slide?.type, isLoading]);

    useEffect(() => {
        if (slide?.type !== 'image') {
            return;
        }

        const startedAt = Date.now();
        const id = setInterval(() => {
            const elapsed = Date.now() - startedAt;
            const next = Math.min(100, (elapsed / IMAGE_DURATION_MS) * 100);
            setProgress(next);
            if (next >= 100) {
                goNext();
            }
        }, PROGRESS_TICK_MS);

        return (): void => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slideIndex, slide?.type]);

    const handleTimeUpdate = (): void => {
        const video = videoRef.current;
        if (!video?.duration) {
            return;
        }
        setProgress((video.currentTime / video.duration) * 100);
    };

    if (!group || !slide) {
        return null;
    }

    return (
        <Modal open onClose={onClose} zIndex={zIndex} title={group.name}>
            <div className={styles.card}>
                <div className={styles.overlay}>
                    <div className={styles['progress-row']}>
                        {group.media.map((s, i) => (
                            <div key={getImagePath(s.url)} className={styles.track}>
                                <div
                                    className={styles.fill}
                                    style={{
                                        width: i < slideIndex ? '100%' : i === slideIndex ? `${progress}%` : '0%',
                                    }}
                                />
                            </div>
                        ))}
                    </div>

                    <div className={styles.header}>
                        <img src={getImageUrl(group.avatar)} alt={group.name} className={styles['header-avatar']} />
                        <span className={styles['header-title']}>{group.name}</span>
                        <div className={styles['header-actions']}>
                            {slide.type === 'video' && (
                                <IconButton
                                    variant="overlay"
                                    size="sm"
                                    className={styles['header-button']}
                                    aria-label={
                                        muted ? t('landing.stories.viewer.unmute') : t('landing.stories.viewer.mute')
                                    }
                                    onClick={() => setMuted((m) => !m)}
                                >
                                    {muted ? <VolumeX /> : <Volume2 />}
                                </IconButton>
                            )}
                            <IconButton
                                variant="overlay"
                                size="sm"
                                className={styles['header-button']}
                                aria-label={t('landing.stories.viewer.close')}
                                onClick={onClose}
                            >
                                <XIcon />
                            </IconButton>
                        </div>
                    </div>
                </div>

                {slide.type === 'image' ? (
                    <img
                        key={getImagePath(slide.url)}
                        src={getImageUrl(slide.url)}
                        alt={slide.description ?? group.name}
                        className={styles.media}
                    />
                ) : (
                    <video
                        key={getImagePath(slide.url)}
                        ref={videoRef}
                        src={getImageUrl(slide.url)}
                        className={styles.media}
                        autoPlay
                        muted={muted}
                        playsInline
                        preload="auto"
                        onTimeUpdate={handleTimeUpdate}
                        onPlaying={() => setIsLoading(false)}
                        onWaiting={() => setIsLoading(true)}
                        onError={goNext}
                        onEnded={goNext}
                    />
                )}

                {slide.type === 'video' && isLoading && (
                    <div className={styles.loading}>
                        <Icon icon={Loader2} size="3xl" className={styles['loading-icon']} />
                    </div>
                )}

                {nextSlide?.type === 'video' && (
                    <video
                        key={`preload-${getImagePath(nextSlide.url)}`}
                        src={getImageUrl(nextSlide.url)}
                        preload="auto"
                        muted
                        playsInline
                        className={styles.preload}
                    />
                )}
                {nextSlide?.type === 'image' && (
                    <img
                        key={`preload-${getImagePath(nextSlide.url)}`}
                        src={getImageUrl(nextSlide.url)}
                        alt=""
                        className={styles.preload}
                    />
                )}

                <button
                    type="button"
                    className={classNames(styles['tap-zone'], styles['tap-zone-prev'])}
                    aria-label={t('landing.stories.viewer.previous')}
                    onClick={goPrev}
                />
                <button
                    type="button"
                    className={classNames(styles['tap-zone'], styles['tap-zone-next'])}
                    aria-label={t('landing.stories.viewer.next')}
                    onClick={goNext}
                />

                {description && <StoryCaption key={slideIndex} html={description} />}
            </div>
        </Modal>
    );
}

function withExternalLinks(html: string | null): string | null {
    const value = html?.trim();

    if (!value) {
        return null;
    }

    if (typeof window === 'undefined') {
        return value;
    }

    const parsed = new DOMParser().parseFromString(value, 'text/html');

    parsed.querySelectorAll('a[href]').forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
    });

    return parsed.body.innerHTML;
}

interface StoryCaptionProps {
    html: string;
}

const StoryCaption = memo(function StoryCaption({ html }: StoryCaptionProps): ReactElement {
    const { t } = useTranslation();
    const safeHtml = useMemo(() => sanitizeHtml(html), [html]);

    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!bodyEl || isExpanded) {
            return;
        }

        const measure = (): void => setIsOverflowing(bodyEl.scrollHeight > bodyEl.clientHeight + 1);

        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(bodyEl);

        if (bodyEl.firstElementChild) {
            observer.observe(bodyEl.firstElementChild);
        }

        return () => observer.disconnect();
    }, [bodyEl, html, isExpanded]);

    return (
        <div
            role="presentation"
            className={classNames(styles.caption, isExpanded && styles['caption-expanded'])}
            onClick={(e) => e.stopPropagation()}
        >
            <div
                ref={setBodyEl}
                className={classNames(
                    styles['caption-body'],
                    isOverflowing && !isExpanded && styles['caption-body-faded'],
                )}
            >
                <EditorText value={safeHtml} size="sm" className={styles['caption-text']} />
            </div>

            {(isOverflowing || isExpanded) && (
                <button type="button" className={styles['caption-toggle']} onClick={() => setIsExpanded((v) => !v)}>
                    {t(
                        isExpanded ? 'landing.stories.description.collapse' : 'landing.stories.description.expand',
                        isExpanded ? 'Mostrar menos' : 'Mostrar descripción completa',
                    )}
                </button>
            )}
        </div>
    );
});
